import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, apiRequest } from '@/lib/api-client';

// ==================== Types ====================

export interface TenantPricing {
  pricePerGym: number;
  currency: string;
  billingInterval: string;
}

export interface SubdomainCheckResult {
  available: boolean;
  subdomain: string;
  message?: string;
}

export interface TenantSignupData {
  businessName: string;
  subdomain: string;
  ownerEmail: string;
  ownerName: string;
  ownerPassword: string;
  gymCount: number;
  color?: string;
  logo?: string;
}

// Branding types
export interface TenantBranding {
  subdomain: string;
  businessName: string;
  primaryColor: string;
  logo: string | null;
}

export interface UpdateBrandingRequest {
  primaryColor?: string;
  logo?: string;
}

export interface UpdateBrandingResponse {
  success: boolean;
  branding: {
    primaryColor: string;
    logo: string | null;
  };
}

export interface TenantSignupRequest {
  tenant: TenantSignupData;
  successUrl: string;
  cancelUrl: string;
}

export interface TenantSignupResponse {
  checkoutUrl: string;
  sessionId: string;
}

export interface TenantSubscription {
  tenantId: string;
  status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing';
  plan: string;
  gymLimit: number;
  gymCount: number;
  pricePerGym: number;
  currency: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
}

export interface SubscriptionUpdateRequest {
  newGymCount: number;
}

export interface SubscriptionCancelRequest {
  immediate?: boolean;
  reason?: string;
}

export interface PortalResponse {
  url: string;
}

// ==================== API Functions ====================

/**
 * Get tenant branding by subdomain (public endpoint)
 */
export async function getTenantBranding(
  subdomain: string
): Promise<TenantBranding> {
  const response = await fetch(
    `https://ioniangyms-mu6nj.ondigitalocean.app/api/v1/tenants/branding/${subdomain}`
  );
  if (!response.ok) {
    throw new Error('Tenant not found');
  }
  return response.json();
}

/**
 * Update tenant branding (authenticated)
 */
export async function updateTenantBranding(
  data: UpdateBrandingRequest
): Promise<UpdateBrandingResponse> {
  const response = await apiRequest<UpdateBrandingResponse>({
    method: 'PATCH',
    url: '/tenants/branding',
    data,
  });
  return response;
}

/**
 * Check if a subdomain is available for registration
 */
export async function checkSubdomainAvailability(
  subdomain: string
): Promise<SubdomainCheckResult> {
  const response = await apiRequest<SubdomainCheckResult>({
    method: 'GET',
    url: `/tenants/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`,
  });
  return response;
}

/**
 * Get pricing information for tenant subscriptions
 */
export async function getTenantPricing(): Promise<TenantPricing> {
  const response = await apiRequest<TenantPricing>({
    method: 'GET',
    url: '/tenants/pricing',
  });
  return response;
}

/**
 * Start tenant signup process - creates Stripe checkout session
 */
export async function createTenantSignup(
  data: TenantSignupRequest
): Promise<TenantSignupResponse> {
  const response = await apiRequest<TenantSignupResponse>({
    method: 'POST',
    url: '/tenants/signup',
    data,
  });
  return response;
}

/**
 * Get current tenant subscription details
 */
export async function getTenantSubscription(): Promise<TenantSubscription> {
  const response = await apiRequest<TenantSubscription>({
    method: 'GET',
    url: '/tenants/subscription',
  });
  return response;
}

/**
 * Update subscription (upgrade/downgrade gym count)
 */
export async function updateTenantSubscription(
  data: SubscriptionUpdateRequest
): Promise<{ message: string; gymLimit: number; plan: string }> {
  const response = await apiRequest({
    method: 'PATCH',
    url: '/tenants/subscription',
    data,
  });
  return response;
}

/**
 * Cancel subscription
 */
export async function cancelTenantSubscription(
  data: SubscriptionCancelRequest
): Promise<{
  message: string;
  canceledAt: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string;
}> {
  const response = await apiRequest({
    method: 'DELETE',
    url: '/tenants/subscription',
    data,
  });
  return response;
}

/**
 * Reactivate a canceled subscription
 */
export async function reactivateTenantSubscription(): Promise<{
  message: string;
}> {
  const response = await apiRequest({
    method: 'POST',
    url: '/tenants/subscription/reactivate',
  });
  return response;
}

/**
 * Get Stripe customer portal URL for billing management
 */
export async function getStripePortalUrl(
  returnUrl: string
): Promise<PortalResponse> {
  const response = await apiRequest<PortalResponse>({
    method: 'GET',
    url: `/tenants/subscription/portal?returnUrl=${encodeURIComponent(
      returnUrl
    )}`,
  });
  return response;
}

// ==================== React Query Hooks ====================

/**
 * Hook to check subdomain availability
 */
export function useCheckSubdomain(subdomain: string, enabled = true) {
  return useQuery({
    queryKey: ['subdomain-check', subdomain],
    queryFn: () => checkSubdomainAvailability(subdomain),
    enabled: enabled && subdomain.length >= 3,
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Hook to get tenant pricing
 */
export function useTenantPricing() {
  return useQuery({
    queryKey: ['tenant-pricing'],
    queryFn: getTenantPricing,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to get current subscription
 */
export function useTenantSubscription() {
  return useQuery({
    queryKey: ['tenant-subscription'],
    queryFn: getTenantSubscription,
    retry: 1,
  });
}

/**
 * Hook to create tenant signup (starts Stripe checkout)
 */
export function useCreateTenantSignup() {
  return useMutation({
    mutationFn: createTenantSignup,
  });
}

/**
 * Hook to update subscription
 */
export function useUpdateTenantSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTenantSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-subscription'] });
    },
  });
}

/**
 * Hook to cancel subscription
 */
export function useCancelTenantSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelTenantSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-subscription'] });
    },
  });
}

/**
 * Hook to reactivate subscription
 */
export function useReactivateTenantSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reactivateTenantSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-subscription'] });
    },
  });
}

/**
 * Hook to get Stripe portal URL
 */
export function useStripePortal() {
  return useMutation({
    mutationFn: (returnUrl: string) => getStripePortalUrl(returnUrl),
  });
}

/**
 * Hook to get tenant branding by subdomain
 */
export function useTenantBrandingQuery(subdomain: string, enabled = true) {
  return useQuery({
    queryKey: ['tenant-branding', subdomain],
    queryFn: () => getTenantBranding(subdomain),
    enabled: enabled && subdomain.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });
}

/**
 * Hook to update tenant branding
 */
export function useUpdateTenantBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTenantBranding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-branding'] });
    },
  });
}
